import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Size as ScreenSize } from '@swf/core/enums';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import useOnNavigate from '@swf/sports/hooks/useOnNavigate';
import type {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef
} from '@swf/sports/types/polymorphicComponent';

import useMobileBodyScrollLock from '../../hooks/useMobileBodyScrollLock';
import useOnMobileBreakpointChange from '../../hooks/useOnMobileBreakpointChange';

import styles from './CollapsibleLayout.scss';

interface Props {
  header: React.ReactNode;
  showMobileHeader: boolean;
  main: React.ReactNode;
}

type CollapsibleLayoutProps<C extends React.ElementType> = PolymorphicComponentPropsWithRef<
  C,
  Props
>;

type CollapsibleLayoutComponent = <C extends React.ElementType = 'div'>(
  props: CollapsibleLayoutProps<C>
) => React.ReactElement | null;

const CollapsibleLayout: CollapsibleLayoutComponent = React.forwardRef<
  HTMLDivElement,
  {
    header: React.ReactNode;
    showMobileHeader: boolean;
    main: React.ReactNode;
  }
>(
  <C extends React.ElementType = 'div'>(
    { as, header, showMobileHeader, main, className, ...restProps }: CollapsibleLayoutProps<C>,
    containerRef?: PolymorphicRef<C>
  ) => {
    const Component = as || 'div';
    const { betSlip } = useDispatch<Dispatch>();
    const isBetSlipOpen = useSelector(select.betSlip.isOpen);
    const { greaterThan } = useScreenSize();
    const isMobile = !greaterThan(ScreenSize.Small);

    // Close when navigating
    useOnNavigate(() => betSlip.betSlipOpened(false));

    // Close when resizing to bigger than mobile
    const onMobileBreakpointChange = useCallback(
      (isMobile: boolean) => !isMobile && betSlip.betSlipOpened(false),
      [betSlip]
    );
    useOnMobileBreakpointChange(onMobileBreakpointChange);

    // Lock body scroll when the layout is open on a small screen
    useMobileBodyScrollLock();

    return (
      <Component
        data-testid="bet-slip"
        ref={containerRef}
        className={classNames(
          styles.layout,
          {
            [styles.betSlipOpen]: isBetSlipOpen,
            [styles.betSlipHidden]: isMobile && !showMobileHeader
          },
          className
        )}
        {...restProps}
      >
        <div className={classNames(styles.header, { [styles.headerBetSlipOpen]: isBetSlipOpen })}>
          {header}
        </div>
        <div
          className={classNames(styles.main, {
            [styles.betSlipOpen]: isBetSlipOpen
          })}
        >
          {main}
        </div>
      </Component>
    );
  }
);

export default CollapsibleLayout;
